import * as React from 'react';
import {
  Select as RACSelect,
  SelectValue as RACSelectValue,
  Button as RACButton,
  Header,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Popover,
  Separator,
  type SelectProps as RACSelectProps,
  type ListBoxItemProps,
  type ListBoxSectionProps,
  type SeparatorProps,
} from 'react-aria-components';
import { DownSmallLine, CheckLine } from '@mingcute/react';

import { cn } from '../lib/cn';

/**
 * Klaret Select (handoff §10.3).
 * Trigger = input-style 36 px, r2, --tvl-border-input border, --tvl-raised fill.
 * Menu: --tvl-raised (--tvl-overlay dark), r3, e2, 4px padding.
 * Options 32px, r2, 8px padding; selected = body-medium + claret check + --tvl-selected.
 * Focus = global 2px ink outline. No accent ring.
 *
 * Compound API mirrors the old Radix shape so consumers need no changes:
 * Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectItem, SelectSeparator
 */

// ---------------------------------------------------------------------------
// Root — accepts the legacy prop shape consumers already use:
//   value, onValueChange, disabled, open, onOpenChange
// ---------------------------------------------------------------------------
export interface SelectProps {
  children?: React.ReactNode;
  /** Controlled selected value (a string key). */
  value?: string;
  /** Called when the selection changes. */
  onValueChange?: (value: string) => void;
  /** Disable the whole control. */
  disabled?: boolean;
  /** Controlled open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Forwarded to RAC Select. */
  name?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
}

export function Select({
  children,
  value,
  onValueChange,
  disabled,
  open,
  onOpenChange,
  name,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  className,
}: SelectProps) {
  return (
    <RACSelect
      selectedKey={value ?? null}
      onSelectionChange={(key) => {
        if (key !== null) onValueChange?.(String(key));
      }}
      isDisabled={disabled}
      isOpen={open}
      onOpenChange={onOpenChange}
      name={name}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={className}
    >
      {children}
    </RACSelect>
  );
}

// ---------------------------------------------------------------------------
// SelectGroup — maps to RAC ListBoxSection (used inside SelectContent)
// ---------------------------------------------------------------------------
export interface SelectGroupProps extends Omit<ListBoxSectionProps<object>, 'children'> {
  children?: React.ReactNode;
  className?: string;
}

export function SelectGroup({ children, className, ...props }: SelectGroupProps) {
  return (
    <ListBoxSection className={cn('py-1', className)} {...props}>
      {children}
    </ListBoxSection>
  );
}

// ---------------------------------------------------------------------------
// SelectGroupLabel — the heading of a SelectGroup.
//
// Must be RAC's <Header>, not a plain element: ListBoxSection builds a COLLECTION from its children,
// and an unrecognised child is either dropped or treated as a focusable option. Header is the one
// node RAC knows is a label, so it renders and is skipped by keyboard navigation.
// ---------------------------------------------------------------------------
export interface SelectGroupLabelProps {
  children?: React.ReactNode;
  className?: string;
}

export function SelectGroupLabel({ children, className }: SelectGroupLabelProps) {
  return (
    <Header
      className={cn(
        'px-2 py-1 text-xs font-medium text-[var(--tvl-ink-3)]',
        className,
      )}
    >
      {children}
    </Header>
  );
}

// ---------------------------------------------------------------------------
// SelectValue — accepts optional children for custom trigger rendering
// ---------------------------------------------------------------------------
export interface SelectValueProps {
  placeholder?: string;
  className?: string;
  children?: React.ReactNode;
}

export function SelectValue({ placeholder, className, children }: SelectValueProps) {
  if (children) {
    // Custom trigger content — render as-is without RAC SelectValue wrapper
    return (
      <span className={cn('flex-1 truncate text-left text-sm text-[var(--tvl-ink)]', className)}>
        {children}
      </span>
    );
  }
  return (
    <RACSelectValue
      className={cn(
        'flex-1 truncate text-left text-sm text-[var(--tvl-ink)] data-[placeholder]:text-[var(--tvl-ink-3)]',
        className,
      )}
    >
      {({ defaultChildren, isPlaceholder }) =>
        isPlaceholder ? <span>{placeholder ?? 'Select…'}</span> : defaultChildren
      }
    </RACSelectValue>
  );
}

// ---------------------------------------------------------------------------
// SelectTrigger
// ---------------------------------------------------------------------------
export interface SelectTriggerProps {
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ className, invalid, children, ...props }, ref) => (
    <RACButton
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'flex h-9 w-full items-center justify-between gap-2 rounded-[var(--tvl-r2)]',
        'border border-[var(--tvl-border-input)] bg-[var(--tvl-raised)] px-3 text-sm',
        'transition-colors duration-[var(--tvl-d-fast)]',
        'hover:border-[var(--tvl-ink-2)]',
        'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        invalid && 'border-[var(--tvl-danger)]',
        className,
      )}
      {...(props as React.ComponentPropsWithoutRef<typeof RACButton>)}
    >
      {children}
      <DownSmallLine size={16} color="var(--tvl-ink-3)" aria-hidden />
    </RACButton>
  ),
);
SelectTrigger.displayName = 'SelectTrigger';

// ---------------------------------------------------------------------------
// SelectContent — the Popover + ListBox
// ---------------------------------------------------------------------------
export interface SelectContentProps {
  className?: string;
  children?: React.ReactNode;
}

export function SelectContent({ className, children }: SelectContentProps) {
  return (
    <Popover
      className={cn(
        'z-[var(--tvl-z-popover)] min-w-[var(--trigger-width)] overflow-hidden',
        'rounded-[var(--tvl-r3)] border border-[var(--tvl-hairline)]',
        'bg-[var(--tvl-raised)] shadow-[var(--tvl-e2)]',
        '[data-theme=dark_&]:bg-[var(--tvl-overlay)]',
        'data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95',
        'data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95',
        className,
      )}
      offset={4}
    >
      <ListBox className="p-1 outline-none">
        {children}
      </ListBox>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// SelectItem
// ---------------------------------------------------------------------------
export interface SelectItemProps
  extends Omit<ListBoxItemProps<object>, 'children' | 'value' | 'id'> {
  children?: React.ReactNode;
  className?: string;
  /** String key / value for this option. */
  value?: string;
  id?: string;
}

export const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ className, children, value, id, ...props }, _ref) => (
    <ListBoxItem
      id={value ?? id}
      textValue={typeof children === 'string' ? children : value}
      className={cn(
        'group relative flex h-8 w-full cursor-pointer select-none items-center justify-between',
        'rounded-[var(--tvl-r2)] px-2 text-sm text-[var(--tvl-ink)] outline-none',
        'hover:bg-[var(--tvl-wash-hover)]',
        'data-[focused]:bg-[var(--tvl-wash-hover)]',
        'data-[selected]:bg-[var(--tvl-selected)] data-[selected]:font-medium',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      {({ isSelected }) => (
        <>
          <span className="flex-1 truncate">{children}</span>
          {isSelected && (
            <CheckLine size={14} color="var(--tvl-accent)" aria-hidden />
          )}
        </>
      )}
    </ListBoxItem>
  ),
);
SelectItem.displayName = 'SelectItem';

// ---------------------------------------------------------------------------
// SelectSeparator — maps to RAC Separator
// ---------------------------------------------------------------------------
export interface SelectSeparatorProps extends SeparatorProps {
  className?: string;
}

export function SelectSeparator({ className, ...props }: SelectSeparatorProps) {
  return (
    <Separator
      className={cn('mx-1 my-1 h-px bg-[var(--tvl-hairline)]', className)}
      {...props}
    />
  );
}
